import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../api.js';
import { Card } from '../components/ui.js';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/**
 * Value-delivered strip on the dashboard: how much underpayment the platform has
 * surfaced to date, and — when the Disputes feature is enabled — how much has been
 * recovered and is still in dispute. The recovered/in-dispute half is gated on the
 * `disputes` entitlement (an app-only, Enterprise feature, like Ask AI).
 */
export function RoiStrip() {
  const { client, baseUrl, token, entitlements } = useAuth();
  const [surfaced, setSurfaced] = useState<number | null>(null);
  const [recovered, setRecovered] = useState<number | null>(null);
  const [inDispute, setInDispute] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    client
      .cumulative({})
      .then((r) => {
        if (live) setSurfaced(r.totals.commissionOwed);
      })
      .catch(() => {});
    if (entitlements.disputes) {
      fetch(`${baseUrl.replace(/\/$/, '')}/disputes`, {
        headers: { authorization: `Bearer ${token}` },
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((j) => {
          if (!live || !j) return;
          const list = (j.data ?? j) as { amountCents: number; status: string }[];
          const sumIf = (pred: (s: string) => boolean) =>
            list.filter((d) => pred(d.status)).reduce((s, d) => s + (d.amountCents ?? 0), 0) / 100;
          setRecovered(sumIf((s) => s === 'recovered'));
          setInDispute(sumIf((s) => s === 'open' || s === 'in_progress'));
        })
        .catch(() => {});
    }
    return () => {
      live = false;
    };
  }, [client, baseUrl, token, entitlements.disputes]);

  // Nothing to celebrate yet (brand-new account) → stay out of the way.
  if (surfaced == null) return null;
  if (surfaced === 0 && !recovered && !inDispute) return null;

  const showDisputes = entitlements.disputes;
  const rate = surfaced > 0 && recovered != null ? Math.round((recovered / surfaced) * 100) : null;

  return (
    <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white dark:border-emerald-900/40 dark:from-emerald-950/20">
      <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
        <Stat
          label="Owed surfaced to date"
          value={money(surfaced)}
          hint="recoverable underpayments we’ve found across your book"
          tone="emerald"
        />
        {showDisputes && (
          <>
            <Stat
              label="Recovered"
              value={recovered == null ? '…' : money(recovered)}
              hint={rate != null ? `${rate}% of surfaced` : 'marked recovered in Disputes'}
              tone="sky"
            />
            <Stat
              label="In dispute"
              value={inDispute == null ? '…' : money(inDispute)}
              hint="open + in progress"
              tone="amber"
            />
            <Link
              to="/disputes"
              className="ml-auto rounded-lg px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100/60 dark:text-emerald-300"
            >
              Open disputes →
            </Link>
          </>
        )}
      </div>
    </Card>
  );
}

const TONE: Record<string, string> = {
  emerald: 'text-emerald-700 dark:text-emerald-300',
  sky: 'text-sky-700 dark:text-sky-300',
  amber: 'text-amber-700 dark:text-amber-300',
};

function Stat({
  label,
  value,
  hint,
  tone,
}: { label: string; value: string; hint: string; tone: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</div>
      <div className={`mt-0.5 text-2xl font-semibold tabular-nums ${TONE[tone] ?? ''}`}>
        {value}
      </div>
      <div className="text-xs text-neutral-400">{hint}</div>
    </div>
  );
}
